/**
 * 영업진행 맨 위 숫자 3개 (v2.203.0 · 2026-09-24)
 *
 * 사용자 요청(개발 계획 「영업진행」): 「맨 위에 당월 발행&판보 금액, 현재기준의 납품 상태의 합계금액,
 * 현재 기준의 발주 상태의 합계금액 이렇게 3가지 값을 보여줘」 · 사용자 결정: **필터와 무관, 전체**.
 *
 * ⭐⭐⭐**숫자를 여기서 따로 세지 않는다 — 대시보드 요약(`/dashboard/summary`)을 그대로 쓴다.**
 *   같은 판정을 두 곳에 적으면 언젠가 갈린다(「화면은 되는데 알림은 안 온다」 · pitfalls 설계 절).
 *   · 이번 달 발행·판보 = 대시보드 「이번 달 매출」의 **영업건 몫**(`monthlyParts` key='sales')
 *     — 계산서 발행일이 이번 달인 발행·판보 건의 제안가(VAT 별도) 합 · 실적증빙 매출 제외
 *   · 납품 / 발주 = 대시보드 「진행단계 분포」의 그 줄(`stages`) — 지금 그 상태인 건 전체 · 실적증빙 제외
 *   쿼리 열쇠도 대시보드와 같다(['dashboard','summary']) — 캐시를 함께 쓰고, 영업건을 고치면
 *   `syncAllData` 가 모든 쿼리를 새로 받게 하므로 이 숫자도 따라 바뀐다.
 * ⭐금액은 서버가 viewer 에게 null 로 보낸다(화면 숨김 ≠ 서버 차단) — 여기서는 *** 로 그릴 뿐이다.
 */

import { useQuery } from '@tanstack/react-query';
import * as dashboardApi from '../lib/dashboard-api';

function fmtKrw(n: number): string {
  return n.toLocaleString('ko-KR');
}

interface Kpi {
  key: string;
  label: string;
  amount: number | null;
  /** 건수 — 대시보드가 안 세는 것은 null(안 그림) */
  count: number | null;
  title: string;
  tone: string;
}

export function SalesKpiStrip() {
  const q = useQuery({
    queryKey: ['dashboard', 'summary'],
    queryFn: () => dashboardApi.getSummary(),
  });

  const month = new Date().getMonth() + 1;
  const d = q.data;
  const stage = (name: string) => d?.stages.find((s) => s.name === name) ?? null;
  const salesPart = d?.metrics.monthlyParts?.find((p) => p.key === 'sales') ?? null;

  const items: Kpi[] = [
    {
      key: 'month',
      label: `${month}월 발행·판보`,
      amount: salesPart ? salesPart.revenue : null,
      count: null,
      title:
        '계산서 발행일이 이번 달인 발행·판보 영업건의 제안가 합계(VAT 별도)\n' +
        '· 실적증빙용 매출은 빠집니다\n· 대시보드 「이번 달 매출」의 영업건 몫과 같은 숫자입니다\n· 아래 필터와 관계없습니다',
      tone: 'text-emerald-700',
    },
    {
      key: 'delivered',
      label: '납품 (계산서 전)',
      amount: stage('납품')?.totalAmount ?? null,
      count: stage('납품')?.count ?? null,
      title:
        '지금 「납품」 상태인 영업건 전체의 제안가 합계(VAT 별도) — 연도와 관계없이\n' +
        '· 실적증빙용 매출은 빠집니다\n· 대시보드 「진행단계 분포」의 납품 줄과 같은 숫자입니다\n· 아래 필터와 관계없습니다',
      tone: 'text-sky-700',
    },
    {
      key: 'ordered',
      label: '발주',
      amount: stage('발주')?.totalAmount ?? null,
      count: stage('발주')?.count ?? null,
      title:
        '지금 「발주」 상태인 영업건 전체의 제안가 합계(VAT 별도) — 연도와 관계없이\n' +
        '· 실적증빙용 매출은 빠집니다\n· 대시보드 「진행단계 분포」의 발주 줄과 같은 숫자입니다\n· 아래 필터와 관계없습니다',
      tone: 'text-indigo-700',
    },
  ];

  return (
    <div className="flex items-stretch gap-2 flex-wrap" aria-label="영업진행 현재 기준 합계">
      {items.map((k) => (
        <div
          key={k.key}
          title={k.title}
          // ⚠**한 줄 · 높이 30px** — 제목(h1, 32px) 줄을 넘지 않게. 두 줄 카드(46px)로 했더니 제목 줄이 14px
          //   높아져 아래 표 높이(100vh - 320px) 계산이 밀렸다(1280 실측).
          className="bg-white border border-slate-200 rounded px-3 py-1 shadow-sm cursor-help flex items-baseline gap-2 whitespace-nowrap"
        >
          <span className="text-xs text-slate-500">
            {k.label}
            {k.count !== null && <span className="text-slate-400"> · {k.count}건</span>}
          </span>
          <span className={`text-sm font-bold num ${k.tone}`}>
            {q.isLoading
              ? '…'
              : q.isError
                ? '-'
                : k.amount === null
                  ? '***'
                  : `₩${fmtKrw(k.amount)}`}
          </span>
        </div>
      ))}
    </div>
  );
}
